import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { generateRoutesConfig, routesConfig as defaultRoutesConfig } from '../routesConfig';
import CompanyService from '../services/company.service';
import { isDealerMember } from '../utils';
import { DealerCompanyFlags, pickDealerCompanyFlags } from '../utils/dealerCompanyFlags';

interface RouteContextType {
  routesConfig: any;
  updateRouteParams: (id: string) => void;
  // lhphcapital-kdm6: the dealer's OWN company flags, which gate its menu. null for staff,
  // and for a dealer until they load (or if they fail to: every flag then reads off).
  dealerFlags: DealerCompanyFlags | null;
  dealerFlagsLoaded: boolean;
}

const RouteContext = createContext<RouteContextType | undefined>(undefined);

export const RouteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [routesConfig, setRoutesConfig] = useState(defaultRoutesConfig);
  const [currentId, setCurrentId] = useState<string>('');
  const [dealerFlags, setDealerFlags] = useState<DealerCompanyFlags | null>(null);
  const [dealerFlagsLoaded, setDealerFlagsLoaded] = useState(false);

  const extractIdFromPath = useCallback((pathname: string): string => {
    const match = pathname.match(/\/(dealers|loans|floorplans|borrowing-base|imports)\/(\d+)/);
    return match ? match[2] : '';
  }, []);

  const updateRouteParams = useCallback((id: string) => {
    setCurrentId(id);
  }, []);

  // Effect to extract and sync currentId from pathname changes
  useEffect(() => {
    const extractedId = extractIdFromPath(location.pathname);
    // Update currentId even if extractedId is empty (to clear the ID when navigating to pages without IDs)
    if (extractedId !== currentId) {
      setCurrentId(extractedId);
    }
  }, [location.pathname, extractIdFromPath, currentId]);

  // lhphcapital-kdm6: a dealer's menu follows its own company's flags, as legacy's does.
  // The company comes from the login, never from the URL.
  const dealerCompanyId = isDealerMember(user ?? null) ? user?.company_id : null;
  useEffect(() => {
    setDealerFlags(null);
    if (!dealerCompanyId) {
      setDealerFlagsLoaded(false);
      return;
    }
    const controller = new AbortController();
    setDealerFlagsLoaded(false);
    CompanyService.getCompanyByRecId(String(dealerCompanyId), controller.signal)
      .then(({ data }) => setDealerFlags(pickDealerCompanyFlags(data)))
      .catch(error => {
        if (controller.signal.aborted) return;
        console.error('Error loading company flags:', error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDealerFlagsLoaded(true);
      });
    return () => controller.abort();
  }, [dealerCompanyId]);

  // Effect to regenerate routes when currentId or user changes
  useEffect(() => {
    if (user) {
      const newConfig = generateRoutesConfig(currentId || '', location, user, dealerFlags);
      // @ts-ignore
      setRoutesConfig(newConfig);
    }
  }, [currentId, user, dealerFlags]);

  return (
    <RouteContext.Provider
      value={{
        routesConfig,
        updateRouteParams,
        dealerFlags,
        dealerFlagsLoaded,
      }}
    >
      {children}
    </RouteContext.Provider>
  );
};

export const useRoute = (): RouteContextType => {
  const context = useContext(RouteContext);
  if (context === undefined) {
    throw new Error('useRoute must be used within a RouteProvider');
  }
  return context;
};
